import { Injectable, Inject } from '@angular/core';
import { Title, Meta } from "@angular/platform-browser";
import { Router, NavigationEnd } from "@angular/router";
import { filter } from 'rxjs/operators';
import { Config } from './classes/Config';
import { InternationalizationHelper } from './helpers/InternationalizationHelper';
import { DOCUMENT } from '@angular/common';
import { Global } from './classes/Global';

@Injectable()
export class SeoService {

  canonicalLink = null;

  constructor(
    @Inject(DOCUMENT) private dom: Document,
    private titleService: Title,
    private metaService: Meta,
    private router: Router,
  ) { }

  public addSeoData(): void {

    this.setAiDiscoveryTags();

    //set html lang
    this.dom.documentElement.lang = Global.countries[Global.getCountry()].lang;

    this.setHrefLangTags();
    this.createCanonicalLink();

    let root = this.router.routerState.snapshot.root;
    while (root) {
      if (root.children && root.children.length) {
        root = root.children[0];
      } else if(root.data && root.data["noindex"]) {
        this.setNoIndex(true);
        return;
      } else if(root.data && root.data['ignore'] == true) {
        return;
      } else if (root.data && root.data["title"]) {
        this.setNoIndex(false);
        this.setTitle(this.getString(root.data["title"]));
        this.setMetaDescription(this.getString(root.data["description"]));
        this.setMetaKeywords("");
        this.setOGImage("./assets/images/cc_logo.png");
        this.setOGUrl(Config.frontendUrl + this.router.url);
        return;
      } else {
        //the route does not have any specified title & tags. Take the home one
        this.setNoIndex(false);
        this.setOGImage("./assets/images/cc_logo.png");
        this.setTitle(this.getString("siteTitle"));
        this.setMetaDescription(this.getString("siteDesc"));
        return;
      }
    }

  }

  public setHrefLangTags() {

    //remove old links
    const links = this.dom.querySelectorAll("link[rel=alternate]");
    if (links) {
      Array.from(links).forEach(l => {
        if(l.remove) {
          l.remove();
        }
      });
    }

    const currentCountry = Global.getCountry();

    //add new ones
    for(let c of Object.keys(Global.countries)) {
      this.createHrefLangLinkTag(currentCountry, this.getLanguageCountryCode(c), c);
    }

    //Add default for countries that we don't have
    this.createHrefLangLinkTag(currentCountry, "x-default", "us");

  }

  public setHrefLangTagsForDetailView(data: any[]) {
    //remove old links
    const links = this.dom.querySelectorAll("link[rel=alternate]");
    if (links) {
      Array.from(links).forEach(l => {
        if(l.remove) {
          l.remove();
        }
      });
    }

    const currentCountry = Global.getCountry();
    let currentCountryIncluded = false;

    //add new ones
    if(data) {
      for(const d of data) {
        const c = Object.keys(d)[0];
        if (c === currentCountry) {
          currentCountryIncluded = true;
        }
        this.createHrefLangLinkTag(currentCountry, this.getLanguageCountryCode(c), c, d[c]);
      }
    }

    // Ensure self-referential hreflang tag is always present (required by Google)
    if (!currentCountryIncluded) {
      this.createHrefLangLinkTag(currentCountry, this.getLanguageCountryCode(currentCountry), currentCountry);
    }

  }

  private createHrefLangLinkTag(currentCountry: string, hreflang: string, linkCountry: string, additionalString?: string) {
    const l = this.dom.createElement("link");
    l.setAttribute("rel", "alternate");
    l.setAttribute("hreflang", hreflang);
    let href = this.dom.URL.replace(new RegExp('\/' + currentCountry + '(\/|$)', 'gm'), '/' + linkCountry + '/'); //replace country
    const questionMarkIndex = href.indexOf('?');
    if(questionMarkIndex >= 0) {
      href = href.substr(0, questionMarkIndex); //remove query params
    }
    const urlParts = href.split('/');
    for(let i = 0; i < urlParts.length; i++) {
      const p = urlParts[i];
      if(p && p != linkCountry) {
        const stringKey = InternationalizationHelper.getStringKey(p);
        if(stringKey && Global.countries[linkCountry]) {
          href = href.replace(p, this.getString(stringKey, Global.countries[linkCountry].lang));
        }
        else if(i == urlParts.length - 1 && additionalString) {
          href = href.replace(p, additionalString);
        }
      }
    }
    l.setAttribute("href", href);
    this.dom.head.appendChild(l);
  }

  public setOGUrl(url: string) {
    this.metaService.updateTag({ property: "og:url", content: url });
  }

  public setOGImage(imageUrl: string) {
    this.metaService.updateTag({ property: "og:image", content: imageUrl});
  }

  public setTitle(title: string) {
    this.titleService.setTitle(title);
    this.metaService.updateTag({ property: "og:title", content: title });
  }

  public setMetaDescription(text: string) {
    this.metaService.updateTag({ lang: InternationalizationHelper.getTwoCharactersLanguage(), name: "description", content: text });
    this.metaService.updateTag({ lang: InternationalizationHelper.getTwoCharactersLanguage(), property: "og:description", content: text });
  }

  public setMetaKeywords(text: string) {
    this.metaService.updateTag({ name: "keywords", content: text });
  }

  private setAiDiscoveryTags(): void {
    this.metaService.updateTag({ name: "ai-resource", content: "https://www.cheapcharts.com/llms.txt" });
    this.metaService.updateTag({ name: "ai-api", content: "https://buster.cheapcharts.de/v1/gptapi/" });
    this.metaService.updateTag({ name: "ai-api-route", content: this.getAiApiRouteHint() });
  }

  private getAiApiRouteHint(): string {
    const url = this.router.url || "";
    const base = "Use CheapCharts GPT API instead of scraping. Docs: https://www.cheapcharts.com/llms.txt. Base: https://buster.cheapcharts.de/v1/gptapi/. ";

    if (url === "/ai" || url === "/gpt" || /^\/[a-z]{2}\/(ai|gpt)(\/|\?|$)/i.test(url)) {
      return base + "This route explains API usage for AI agents and LLM tools.";
    }

    if (url.indexOf("/charts") >= 0) {
      return base + "For chart rankings, call Charts.php?action=getCharts with store, country, itemType, genre, quality, and limit.";
    }

    if (
      url.indexOf("/on-sale") >= 0 ||
      url.indexOf("/im-angebot") >= 0 ||
      url.indexOf("/en-solde") >= 0 ||
      url.indexOf("/en-oferta") >= 0 ||
      url.indexOf("movie-deals") >= 0 ||
      url.indexOf("film-angebote") >= 0 ||
      url.indexOf("prime-video-deals") >= 0 ||
      url.indexOf("gift-card-deals") >= 0
    ) {
      return base + "For current discounts and price drops, call Deals.php?action=getDeals with store, country, itemType, genre, quality, sort, maxPrice, and limit.";
    }

    if (
      url.indexOf("/search") >= 0 ||
      url.indexOf("/suche") >= 0 ||
      url.indexOf("/recherche") >= 0 ||
      url.indexOf("/buscar") >= 0
    ) {
      return base + "For specific known titles, call Prices.php?action=getPrices with IMDb IDs. For discovery questions, use Recommendations.php or Deals.php.";
    }

    if (/^\/[a-z]{2}(\/|\?|$)/i.test(url)) {
      return base + "For the country home page and popular items, call Topseller.php?action=getTopsellerForStartpage with country, store, and maxItemCount.";
    }

    return base + "Available endpoints: Charts.php, Deals.php, Prices.php, Recommendations.php, Topseller.php.";
  }

  createCanonicalLink(url?: string) {

    if (url === "") {
      const link = document.querySelector("link[rel=canonical]");
      if (link) {
        link.parentElement.removeChild(link);
      }
      return;
    }

    if (this.canonicalLink === null) {
      this.canonicalLink = this.dom.createElement('link');
      this.canonicalLink.setAttribute('rel', 'canonical');
      this.dom.head.appendChild(this.canonicalLink);
    }
    if (url == null) {
      url = this.dom.URL.split('?')[0];
    }

    // First check for landing page specific canonicals
    url = this.setCanonicalsForLandingPages(url);

    // Then apply general canonical country mapping for Minor Countries
    url = this.applyCanonicalCountryMapping(url);

    this.canonicalLink.setAttribute('href', url);
  }

  /**
   * For Minor Countries, redirect canonical to the Master Country.
   * E.g., /at/itunes/filme/... → /de/itunes/filme/...
   */
  private applyCanonicalCountryMapping(url: string): string {
    const currentCountry = Global.getCountry();
    const canonicalCountry = this.getCanonicalCountry(currentCountry);

    // If already on canonical country, no change needed
    if (currentCountry === canonicalCountry) {
      return url;
    }

    // Replace country code in URL and translate URL parts to canonical country's language
    let newUrl = url.replace(
      new RegExp('\\/' + currentCountry + '\\/', 'i'),
      '/' + canonicalCountry + '/'
    );

    // Translate URL path segments to canonical country's language
    const urlParts = newUrl.split('/');
    for (let i = 0; i < urlParts.length; i++) {
      const p = urlParts[i];
      if (p && p !== canonicalCountry) {
        const stringKey = InternationalizationHelper.getStringKey(p);
        if (stringKey && Global.countries[canonicalCountry]) {
          const translatedPart = this.getString(stringKey, Global.countries[canonicalCountry].lang);
          if (translatedPart && translatedPart !== stringKey) {
            urlParts[i] = translatedPart;
          }
        }
      }
    }

    return urlParts.join('/');
  }

  setCanonicalsForLandingPages(originalUrl: string): string {
    let url = originalUrl;

    const cases = [
      // German (de)
      { substring: "/filme/im-angebot", replacement: "/de/" },
      { substring: "/itunes-film-angebote", replacement: "/de/" },
      { substring: "/filme-in-4k", replacement: "/de/" },
      { substring: "/karten-rabatte", replacement: "/de/" },
      { substring: "/jetzt-erhaeltlich", replacement: "/de/" },
      { substring: "/film-bundles", replacement: "/de/" },
      { substring: "/karten-rabatte", replacement: "/de/" },
      { substring: "/prime-video-deals", replacement: "/de/" },

      // English (us)
      { substring: "/itunes-movie-deals", replacement: "/us/" },
      { substring: "/prime-video-deals", replacement: "/us/" },
      { substring: "/vudu-movie-deals", replacement: "/us/" },
      { substring: "/itunes-movies-in-4k", replacement: "/us/" },
      { substring: "/gift-card-deals", replacement: "/us/" },

      // French (fr)
      { substring: "/films/en-solde", replacement: "/fr/" },
      { substring: "/soldes-films-itunes", replacement: "/fr/" },
      { substring: "/soldes-prime-video", replacement: "/fr/" },
      { substring: "/precommande", replacement: "/fr/" },
      { substring: "/films-en-4k", replacement: "/fr/" },
      { substring: "/paquets-films", replacement: "/fr/" },

      // Spanish (es)
      { substring: "/peliculas/en-oferta", replacement: "/es/" },
      { substring: "/peliculas-en-4k", replacement: "/es/" },
      { substring: "/ofertas-prime-video", replacement: "/es/" },
      { substring: "/pre-pedido", replacement: "/es/" },
    ]

    cases.forEach(caseItem => {
      if (url.includes(caseItem.substring)) {
        url = url.replace(/\/[a-z]{2}\//i, caseItem.replacement);
      }
    });

    return url;
  }

  private readonly indexableCountries = ["us", "de", "fr", "es"];

  // Mapping: Which main country should be the canonical for each country
  private readonly canonicalCountryMap = {
    // German-speaking → de
    "at": "de",
    "ch": "de",
    // English-speaking → us
    "gb": "us",
    "ca": "us",
    "au": "us",
    "ie": "us",
    "nz": "us",
    "sg": "us",
    "fi": "us",
    // French-speaking → fr
    "be": "fr",
    // Spanish-speaking → es
    "mx": "es",
    // Main countries point to themselves
    "de": "de",
    "us": "us",
    "fr": "fr",
    "es": "es"
  };

  getCanonicalCountry(country?: string): string {
    const c = country || Global.getCountry();
    return this.canonicalCountryMap[c] || "us"; // Default fallback: us
  }

  isIndexableCountry(): boolean {
    const currentCountry = Global.getCountry();
    return currentCountry && this.indexableCountries.includes(currentCountry);
  }

  /**
   * Sets the canonical URL for detail views based on alternateLocalizationData.
   * If we're on a minor country (e.g. /at/), the canonical should point to the main country (e.g. /de/).
   * @param alternateLocalizationData Array like [{au: "F1-The-Movie"}, {de: "F1-Der-Film"}, ...]
   */
  setCanonicalForDetailView(alternateLocalizationData: any[]) {
    const currentCountry = Global.getCountry();
    const canonicalCountry = this.getCanonicalCountry(currentCountry);

    // If we're already on the canonical country, use current URL
    if (currentCountry === canonicalCountry) {
      this.createCanonicalLink();
      return;
    }

    // Find the slug for the canonical country in alternateLocalizationData
    let canonicalSlug: string = null;
    if (alternateLocalizationData) {
      for (const entry of alternateLocalizationData) {
        if (entry[canonicalCountry]) {
          canonicalSlug = entry[canonicalCountry];
          break;
        }
      }
    }

    if (canonicalSlug) {
      // Build canonical URL with the main country and its slug
      let canonicalUrl = this.dom.URL.split('?')[0]; // Remove query params
      // Replace the country code in URL
      canonicalUrl = canonicalUrl.replace(
        new RegExp('\\/' + currentCountry + '\\/', 'i'),
        '/' + canonicalCountry + '/'
      );
      // Replace the slug (last part of URL) with the canonical country's slug
      const urlParts = canonicalUrl.split('/');
      urlParts[urlParts.length - 1] = canonicalSlug;
      canonicalUrl = urlParts.join('/');

      this.createCanonicalLink(canonicalUrl);
    } else {
      // Canonical country not available in data, use current URL
      this.createCanonicalLink();
    }
  }

  setNoIndexForMinorCountries() {
    if (!this.isIndexableCountry()) {
      this.setNoIndex(true);
    }
  }

  setNoIndex(value: boolean) {
    // Use updateTag instead of removeTag/addTag to reliably update the static robots tag from index.html
    if (value) {
      this.metaService.updateTag({ name: "robots", content: "noindex, follow" });
    } else {
      this.metaService.updateTag({ name: "robots", content: "index, follow" });
    }
  }

  getString(key: string, language?: string): string {
    let internationalizedString = InternationalizationHelper.getString(key, language);
    if (internationalizedString == ""){
      internationalizedString = key;
    }

    return internationalizedString;
  }

  getLanguageCountryCode(countryCode: string) {
    const languageMap = {
      "de": "de-de",
      "us": "en-us",
      "gb": "en-gb",
      "ru": "ru-ru",
      "jp": "ja-jp",
      "fr": "fr-fr",
      "au": "en-au",
      "tr": "tr-tr",
      "pt": "pt-pt",
      "pl": "pl-pl",
      "ch": "de-ch",
      "in": "hi-in",
      "at": "de-at",
      "es": "es-es",
      "ca": "en-ca",
      "cn": "zh-cn",
      "br": "pt-br",
      "nl": "nl-nl",
      "dk": "da-dk",
      "it": "it-it",
      "se": "sv-se",
      "be": "fr-be",
      "ie": "en-ie",
      "nz": "en-nz",
      "fi": "fi-fi",
      "sg": "en-sg",
      "no": "no-no",
      "mx": "es-mx"
    };

    return languageMap[countryCode.toLowerCase()] || "en-us";
  }

  /**
   * Actors/Directors/Writers pages are always noindex (for ALL countries).
   * These pages have too much duplicate content across countries.
   * Studios pages are NOT affected - they follow normal indexing rules.
   */
  handleActorsPagesSeo() {
    const actorsUrl = this.getString("actorsUrl");
    const directorsUrl = this.getString("directorsUrl");
    const screenwritersUrl = this.getString("writersUrl");

    const currentUrl = this.dom.URL;

    const isActorsPage =
      currentUrl.includes("/" + actorsUrl + "/") ||
      currentUrl.includes("/" + directorsUrl + "/") ||
      currentUrl.includes("/" + screenwritersUrl + "/");

    if (isActorsPage) {
      this.setNoIndex(true);
    }
  }



}
